// Copyright (c) StrangeBee 2023

import AssigneeInfo from '@Molecules/AssigneeInfo/AssigneeInfo';
import { ExtractProps } from '@Molecules/Details/Details';
import { FormatDate } from '@Molecules/Format';
import React, { ReactElement } from 'react';
import { CustomStatusOutput, CaseStatusStage, SeverityValues } from 'thehive-sdk';
import { Space } from 'antd';
import { Link } from 'react-router-dom';
import { classList } from '@Helpers/className';
import { ColorLabel } from '@Atoms/Info/ColorLabel/ColorLabel';
import { HTMLColors } from '@Constants/colors';
import { convertToLocaleId } from '@Helpers/locale';
import { ICONS } from '@Constants/iconsApp';
import DisplayTag from '@Organisms/DisplayTag/DisplayTag';
import { cutString } from '@Helpers/string';
import { SeverityTag } from '@Atoms/Info/SeverityTag';

interface ExtractDataProps {
    locale: (...args: any[]) => any;
}

export const extractData = ({ locale }: ExtractDataProps): Record<string, ExtractProps> => ({
    '#computeTitle': {
        name: '',
        render: ({
            title,
            path,
            caseNumber,
            severity,
        }: {
            title: string;
            path: string;
            caseNumber: number;
            severity: SeverityValues;
        }): ReactElement => (
            <div className="result-card__title">
                <Space>
                    <SeverityTag severity={severity} />
                    <Link to={path}>{`#${caseNumber} - ${title}`}</Link>
                </Space>
            </div>
        ),
    },
    '#computeDate': {
        name: '',
        render: ({ date, assignee }: { date: number; assignee: string }): ReactElement => (
            <div className="result-card__date">
                <FormatDate icon date={date} />
                {assignee && <AssigneeInfo assignee={assignee} />}
            </div>
        ),
    },
    '#computeStatus': {
        name: '',
        isForce: false,
        render: ({
            status,
            startDate,
            endDate,
            _createdAt,
        }: {
            status: CustomStatusOutput;
            startDate: number;
            endDate: number;
            _createdAt: number;
        }): ReactElement | null =>
            status ? (
                <div className="case-result-card__status-container">
                    <div
                        className={classList('case-result-card__status', {
                            open: status?.stage !== CaseStatusStage.CLOSED,
                            closed: status?.stage === CaseStatusStage.CLOSED,
                        })}
                        style={{ [`--color-status` as string]: status?.colour || HTMLColors.DARK_GRAY }}
                    >
                        <ColorLabel
                            label={locale({
                                id: `case.data.status.${convertToLocaleId(status?.value)}`,
                                defaultMessage: status?.value,
                            })}
                            color={status?.colour || HTMLColors.DARK_GRAY}
                            className={classList('status-tag')}
                            type="fill"
                        />

                        <div className="case-list__status__date">
                            <ICONS.ClockCircleOutlined />
                            <FormatDate
                                date={(status?.stage !== CaseStatusStage.CLOSED ? startDate : endDate) || _createdAt}
                                onlyFrom
                            />
                        </div>
                    </div>

                    {status?.stage === CaseStatusStage.CLOSED && (
                        <div className="case-list-title__closed">
                            {locale('case.list.table.description.closed')} <FormatDate date={endDate} />{' '}
                        </div>
                    )}
                </div>
            ) : null,
    },
    '#computeDescription': {
        name: '',
        isForce: false,
        render: (description: string): ReactElement => (
            <div className="result-card__description">
                {description === 'N/A' ? locale('common.noDescription') : cutString(description, 140)}
            </div>
        ),
    },
    '#computeTags': {
        name: '',
        isForce: false,
        render: (tags: string[]): ReactElement => (
            <DisplayTag.List data={tags} limit={5}>
                {(tag) => <DisplayTag tag={tag} />}
            </DisplayTag.List>
        ),
    },
});
